<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const copyBtnText = ref('Copy IP')
const copyBtnIcon = ref(true)

const stepCopyText = ref('Copy IP')
const stepCopied = ref(false)

const steamGameUrl = 'https://store.steampowered.com/app/2399830/ARK_Survival_Ascended/'

// Dynamic content from CMS
const content = ref({
  hero: {
    title1: 'CONQUER THE',
    titleHighlight: 'PREHISTORIC',
    title2: 'WORLD',
    description: 'Wake up on the shore of a mysterious island. Hunt, gather resources, craft tools, grow crops, and build shelters to survive. Tame and breed dinosaurs to create your army.'
  },
  serverCard: { ip: 'play.arkserver.com:27015' },
  stats: { online: '428', tamed: '156K', tribes: '3,240', rates: 'x10' },
  features: [
    { title: 'Boosted Rates', desc: '10x harvesting, 15x taming, and 25x breeding for faster progression' },
    { title: 'Custom Mods', desc: 'Structures Plus, Dino Storage, and more quality of life mods' },
    { title: 'Active Admins', desc: '24/7 active staff team to ensure fair play and quick support' },
    { title: 'Great Community', desc: 'Join thousands of friendly players in our growing community' }
  ],
  rules: [
    { title: 'General Conduct', items: ['Be respectful to all players', 'No harassment or hate speech', 'English only in global chat'] },
    { title: 'Base Building', items: ['No blocking caves or obelisks', 'Maximum 2 bases per tribe', 'Keep bases reasonably sized'] },
    { title: 'PvP Rules', items: ['No offline raiding on weekdays', 'Raid window: Fri-Sun 18:00-23:00', 'No spawn camping'] }
  ],
  howItWorks: [
    { title: 'Get ARK on Steam', desc: 'Purchase ARK: Survival Evolved on Steam. An official license is required to access our server. Download and install the game.' },
    { title: 'Connect to Server', desc: 'Launch ARK and open the server browser. Search for our server or use direct connect with our IP address to join instantly.' },
    { title: 'Start Surviving', desc: "That's it! Spawn in and start your adventure — tame dinos, build bases, raid enemies! Check out our store for exclusive items." }
  ],
  footer: { copyright: '© 2026 ARK Server. All rights reserved.' }
})

// Fallback copy function for HTTP
const fallbackCopy = (text) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    return true
  } catch (e) {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

const copyToClipboard = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (e) {
      return fallbackCopy(text)
    }
  }
  return fallbackCopy(text)
}

const copyIP = async () => {
  const ip = content.value.serverCard?.ip || 'play.arkserver.com:27015'
  const success = await copyToClipboard(ip)
  if (success) {
    copyBtnText.value = 'Copied!'
    copyBtnIcon.value = false
  } else {
    copyBtnText.value = 'Failed'
  }
  setTimeout(() => {
    copyBtnText.value = 'Copy IP'
    copyBtnIcon.value = true
  }, 2000)
}

const copyServerIPFromStep = async () => {
  const ip = content.value.serverCard?.ip || 'play.arkserver.com:27015'
  const success = await copyToClipboard(ip)
  if (success) {
    stepCopied.value = true
    stepCopyText.value = 'Copied!'
  } else {
    stepCopyText.value = 'Copy failed'
  }
  setTimeout(() => {
    stepCopied.value = false
    stepCopyText.value = 'Copy IP'
  }, 2000)
}

// Load content from API
const loadContent = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/site-content`)
    if (response.data && Object.keys(response.data).length > 0) {
      content.value = { ...content.value, ...response.data }
    }
  } catch (e) {
    // Use defaults
  }
}

// Active nav link on scroll
onMounted(() => {
  loadContent()

  const handleScroll = () => {
    const sections = document.querySelectorAll('section[id]')
    const navLinks = document.querySelectorAll('.nav-link')
    let current = 'home'

    const markerY = 140
    sections.forEach(section => {
      const rect = section.getBoundingClientRect()
      if (rect.top <= markerY && rect.bottom > markerY) {
        current = section.getAttribute('id') || 'home'
      }
    })

    navLinks.forEach(link => {
      link.classList.remove('active')
      const href = link.getAttribute('href') || ''
      const hashIndex = href.indexOf('#')
      const hrefHash = hashIndex >= 0 ? href.slice(hashIndex + 1) : ''
      if (hrefHash && hrefHash === current) {
        link.classList.add('active')
        return
      }
      if (!hrefHash && link.hasAttribute('href') && (href === '/' || href === '') && current === 'home') {
        link.classList.add('active')
      }
    })
  }
  
  window.addEventListener('scroll', handleScroll, { passive: true })
  document.addEventListener('scroll', handleScroll, { passive: true, capture: true })
  window.addEventListener('resize', handleScroll, { passive: true })
  handleScroll()
  
  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('scroll', handleScroll, true)
    window.removeEventListener('resize', handleScroll)
    // Clear stale .active from nav-links so they don't persist on other pages
    document.querySelectorAll('.nav-link.active').forEach(l => l.classList.remove('active'))
  })
})
</script>

<template>
    <!-- ==================== HERO SECTION ==================== -->
    <section id="home" class="hero">
        <div class="hero-container">
            <div class="hero-content">
                <h1 class="hero-title">
                    {{ content.hero?.title1 || 'CONQUER THE' }}<br>
                    <span class="highlight">{{ content.hero?.titleHighlight || 'PREHISTORIC' }}</span><br>
                    {{ content.hero?.title2 || 'WORLD' }}
                </h1>

                <p class="hero-desc">
                    {{ content.hero?.description || 'Wake up on the shore of a mysterious island.' }}
                </p>

                <div class="server-card">
                    <div class="server-ip">{{ content.serverCard?.ip || 'play.arkserver.com:27015' }}</div>
                    <button class="btn-copy" @click="copyIP">
                        <svg v-if="copyBtnIcon" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                        <svg v-else viewBox="0 0 24 24" width="16" height="16"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                        {{ copyBtnText }}
                    </button>
                </div>

                <div class="cta-grid cta-grid-3">
                    <a href="#how-it-works" class="cta-card cta-primary">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                        <span>Start Playing</span>
                    </a>
                    <a href="#features" class="cta-card">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                        <span>Features</span>
                    </a>
                    <a href="#rules" class="cta-card">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        <span>Rules</span>
                    </a>
                </div>
            </div>

            <!-- Promo Block: Desktop — fancy card -->
            <RouterLink to="/market" class="market-promo desktop-promo">
                <div class="market-promo-bg">
                    <div class="market-promo-orb market-promo-orb-1"></div>
                    <div class="market-promo-orb market-promo-orb-2"></div>
                </div>
                <div class="market-promo-content">
                    <div class="promo-slide-inner">
                        <div class="market-promo-label">ARK</div>
                        <h3 class="market-promo-title"><span>SHOP</span> <span>NOW</span></h3>
                        <div class="market-promo-divider"></div>
                        <p class="market-promo-desc">Exclusive dinos, kits & gear.<br>Pay with ArkGems.</p>
                        <div class="market-promo-cta">
                            <span>Browse Market</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </div>
                    </div>
                </div>
                <div class="market-promo-border-glow"></div>
            </RouterLink>

            <!-- Promo Block: Mobile — simple CTA button -->
            <div class="mobile-promo-btns">
                <RouterLink to="/market" class="mobile-promo-btn mobile-promo-market">
                    <span>BROWSE MARKET</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </RouterLink>
            </div>
        </div>
    </section>

    <!-- ==================== FEATURES SECTION ==================== -->
    <section id="features" class="section">
        <div class="section-inner">
            <div class="section-header">
                <h2 class="section-title">SERVER <span class="highlight">FEATURES</span></h2>
                <p class="section-desc">Experience ARK like never before with our custom features and quality of life improvements</p>
            </div>

            <div class="features-grid">
                <div v-for="(feature, index) in content.features" :key="index" class="feature-card">
                    <div class="feature-icon">
                        <svg v-if="index === 0" viewBox="0 0 24 24"><path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/></svg>
                        <svg v-else-if="index === 1" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                        <svg v-else-if="index === 2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        <svg v-else viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                    </div>
                    <h3 class="feature-title">{{ feature.title }}</h3>
                    <p class="feature-desc">{{ feature.desc }}</p>
                </div>
            </div>
        </div>
    </section>

    <!-- ==================== RULES SECTION ==================== -->
    <section id="rules" class="section">
        <div class="section-inner">
            <div class="section-header">
                <h2 class="section-title"><span class="highlight">RULES</span></h2>
                <p class="section-desc">Follow these rules to ensure a fair and enjoyable experience for everyone</p>
            </div>

            <div class="rules-grid">
                <div v-for="(rule, index) in content.rules" :key="index" class="rule-card">
                    <div class="rule-header">
                        <div class="rule-icon">
                            <svg v-if="index === 0" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                            <svg v-else-if="index === 1" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                            <svg v-else viewBox="0 0 24 24"><path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/></svg>
                        </div>
                        <h3 class="rule-title">{{ rule.title }}</h3>
                    </div>
                    <ul class="rule-list">
                        <li v-for="(item, itemIndex) in rule.items" :key="itemIndex">{{ item }}</li>
                    </ul>
                </div>
            </div>
        </div>
    </section>

    <!-- ==================== HOW IT WORKS SECTION ==================== -->
    <section id="how-it-works" class="section">
        <div class="section-inner">
            <div class="section-header">
                <h2 class="section-title">START <span class="highlight">PLAYING</span></h2>
                <p class="section-desc">Join our server and get exclusive items from the shop</p>
            </div>

            <div class="how-it-works-content">
                <div class="steps-container">
                    <div v-for="(step, index) in content.howItWorks" :key="index" class="step-card">
                        <div class="step-content">
                            <div class="step-icon">
                                <svg v-if="index === 0" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                                <svg v-else-if="index === 1" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                <svg v-else viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </div>
                            <h3 class="step-title">{{ step.title }}</h3>
                            <p class="step-desc">{{ step.desc }}</p>

                            <div v-if="index === 0" class="step-inline">
                                <a :href="steamGameUrl" target="_blank" rel="noopener" class="step-inline-link">
                                    <span>Open on Steam</span>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M14 3h7v7"/>
                                        <path d="M10 14L21 3"/>
                                    </svg>
                                </a>
                            </div>

                            <div v-else-if="index === 1" class="step-inline">
                                <button class="step-inline-link step-inline-link-copy" type="button" @click="copyServerIPFromStep" :class="{ copied: stepCopied }">
                                    <span>{{ stepCopyText }}</span>
                                    <svg v-if="!stepCopied" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2"/>
                                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                    </svg>
                                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <RouterLink to="/market" class="btn-shop">Market</RouterLink>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* Scoped styles are minimal - main styles in landing.css */
</style>
